/**
 * Conocimiento configurable desde el dashboard (AssistantTraining → business.assistant_training).
 * El menú público ya incluye `business` con este objeto; aquí lo normalizamos y matcheamos consultas.
 */

function norm(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();
}

function tokens(s) {
  const t = norm(s)
    .split(/[^a-z0-9áéíóúñ]+/)
    .filter((x) => x.length > 1);
  return new Set(t);
}

function tokenOverlapScore(userNorm, questionNorm) {
  const a = tokens(userNorm);
  const b = tokens(questionNorm);
  if (!a.size || !b.size) return 0;
  let inter = 0;
  for (const x of a) {
    if (b.has(x)) inter += 1;
  }
  const denom = Math.min(a.size, b.size);
  return denom ? inter / denom : 0;
}

/**
 * @param {object|null|undefined} business — menuData.business
 * @returns {null|{ custom_info: string, ingredients_info: string, allergies_info: string, story: string, faq: {question?: string, answer?: string}[] }}
 */
export function getAssistantTraining(business) {
  const raw = business?.assistant_training;
  if (!raw || typeof raw !== 'object') return null;
  const faqIn = Array.isArray(raw.faq) ? raw.faq : [];
  const faq = faqIn
    .map((row) => ({
      question: String(row?.question ?? '').trim(),
      answer: String(row?.answer ?? '').trim(),
    }))
    .filter((row) => row.question && row.answer);
  const out = {
    custom_info: String(raw.custom_info ?? '').trim(),
    ingredients_info: String(raw.ingredients_info ?? '').trim(),
    allergies_info: String(raw.allergies_info ?? '').trim(),
    story: String(raw.story ?? '').trim(),
    faq,
  };
  if (
    !out.custom_info &&
    !out.ingredients_info &&
    !out.allergies_info &&
    !out.story &&
    !out.faq.length
  ) {
    return null;
  }
  return out;
}

function clipText(s, max = 3500) {
  const t = String(s || '').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function bestFaqMatch(trimmed, n, faq) {
  let best = null;
  let bestScore = 0;
  for (const row of faq) {
    const qn = norm(row.question);
    const un = n || norm(trimmed);
    if (!qn || !un) continue;
    if (un.includes(qn) || qn.includes(un)) {
      return { score: 1, row };
    }
    const ov = tokenOverlapScore(un, qn);
    if (ov >= 0.38 && ov > bestScore) {
      bestScore = ov;
      best = row;
    } else if (ov >= 0.22 && ov > bestScore) {
      const ut = tokens(un);
      const qt = tokens(qn);
      let inter = 0;
      for (const x of ut) {
        if (qt.has(x)) inter += 1;
      }
      if (inter >= 2 && ov > bestScore) {
        bestScore = ov;
        best = row;
      }
    }
  }
  if (best && bestScore >= 0.22) return { score: bestScore, row: best };
  return null;
}

/**
 * Respuestas por bloques de training + FAQ. Se usa antes del fallback humano.
 * @returns {object|null} mismo shape que otras respuestas de buildFreeTextAnswer
 */
export function tryAssistantTrainingAnswer(menuData, trimmed, n, defaultFallback) {
  const business = menuData?.business || {};
  const t = getAssistantTraining(business);
  if (!t) return null;

  const un = n || norm(trimmed);

  const faqHit = bestFaqMatch(trimmed, un, t.faq);
  if (faqHit?.row) {
    return {
      text: `💬 ${clipText(faqHit.row.answer, 3200)}`,
      products: null,
      fallbackKeys: defaultFallback,
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  if (
    /\b(historia|historias|origen|fundacion|fundación|quienes son|quiénes son|sobre el local|sobre el restaurant|sobre ustedes|dueño|dueña|dueños|emprend|empezaron)\b/.test(
      un
    ) &&
    t.story
  ) {
    return {
      text: `📖 ${clipText(t.story, 3200)}`,
      products: null,
      fallbackKeys: defaultFallback,
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  if (
    /\b(ingredientes frescos|calidad de ingredientes|proveedores|de donde compran|dónde compran|elaboracion|elaboración propia|hecho en casa)\b/.test(
      un
    ) &&
    t.ingredients_info
  ) {
    return {
      text: `🥘 ${clipText(t.ingredients_info, 3200)}`,
      products: null,
      fallbackKeys: defaultFallback,
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  if (
    /\b(politica de alergias|política de alergias|manejan alergias|informacion sobre alergias|información sobre alergias|cuidado con alergias|trazas)\b/.test(
      un
    ) &&
    t.allergies_info
  ) {
    return {
      text: `⚠️ ${clipText(t.allergies_info, 3200)}`,
      products: null,
      fallbackKeys: defaultFallback,
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  if (
    /\b(recomiendan|recomendacion|recomendación|especialidad|especialidades|que destacan|qué destacan|dato del local|info del local|informacion del negocio|información del negocio)\b/.test(
      un
    ) &&
    t.custom_info
  ) {
    return {
      text: `✨ ${clipText(t.custom_info, 3200)}`,
      products: null,
      fallbackKeys: defaultFallback,
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  return null;
}
